import { highestAccessLevel } from '@omnea-digital/authz-model'
import type {
  AccessLevel,
  GrantedAccessLevel,
  PermissionOf,
  PermissionSetEntity,
  PermissionSetPermission,
} from '@omnea-digital/authz-model'

type LadderEntry = {
  [Entity in PermissionSetEntity]: { area: string; entity: Entity; permissions: Record<GrantedAccessLevel, PermissionOf<Entity>> }
}[PermissionSetEntity]

// One toggle per level: the model's ladder (manage confers edit, edit confers view) supplies the rest.
const ACCESS_LADDERS = [
  {
    area: 'documents',
    entity: 'request',
    permissions: { view: 'view_documents', edit: 'edit_documents', full: 'manage_documents' },
  },
  {
    area: 'documents',
    entity: 'supplier',
    permissions: { view: 'view_related_documents', edit: 'edit_documents', full: 'manage_documents' },
  },
  {
    area: 'documents',
    entity: 'agreement',
    permissions: { view: 'view_documents', edit: 'edit_documents', full: 'manage_documents' },
  },
  { area: 'tprm', entity: 'request', permissions: { view: 'view_tprm', edit: 'edit_tprm', full: 'manage_tprm' } },
  { area: 'tprm', entity: 'supplier', permissions: { view: 'view_tprm', edit: 'edit_tprm', full: 'manage_tprm' } },
] as const satisfies readonly LadderEntry[]

/** One area's ladder on one entity: the permission each level toggles on a set. */
export type AccessLadder = (typeof ACCESS_LADDERS)[number]

export type AccessArea = AccessLadder['area']

export const ladderPermissions = (ladder: AccessLadder): readonly PermissionSetPermission[] => Object.values(ladder.permissions)

/**
 * The ladders an entity's sets hold, each rendered as one picker in place of its toggles. A ladder renders only
 * when every one of its toggles is offered, so a row cut down to one toggle stays that toggle.
 */
export const accessLaddersOf = (
  entity: PermissionSetEntity,
  offered: readonly PermissionSetPermission[]
): readonly AccessLadder[] =>
  ACCESS_LADDERS.filter(
    (ladder) => ladder.entity === entity && ladderPermissions(ladder).every((permission) => offered.includes(permission))
  )

export const isDocumentEntity = (entity: PermissionSetEntity): boolean =>
  ACCESS_LADDERS.some((ladder) => ladder.entity === entity && ladder.area === 'documents')

/** The highest level the permissions hold, so a set saved before the ladder still reads as one level. */
export const accessLevelOf = (ladder: AccessLadder, permissions: readonly PermissionSetPermission[]): AccessLevel =>
  highestAccessLevel((level) => permissions.includes(ladder.permissions[level]))

export const withAccessLevel = (
  ladder: AccessLadder,
  permissions: readonly PermissionSetPermission[],
  level: AccessLevel
): PermissionSetPermission[] => {
  const toggles = ladderPermissions(ladder)
  const others = permissions.filter((permission) => !toggles.includes(permission))
  return level === 'none' ? others : [...others, ladder.permissions[level]]
}
